import React, { useRef, useEffect, useMemo } from 'react';
import MessageGroup from './MessageGroup';
import TypingIndicator from './TypingIndicator';
import type { Message } from '../../types/chat';
import { AnimatePresence, motion } from 'framer-motion';

interface MessageListProps {
  messages: Message[];
  isTyping: boolean;
  typingName: string;
  conversation?: {
    name: string;
    image: string;
  };
  onDelete?: (messageId: string) => void;
  onEdit?: (messageId: string, newContent: string) => void;
  onReply?: (message: Message) => void;
  onLike?: (messageId: string) => void;
  editingId?: string | null;
  onStartEdit?: (messageId: string | null) => void;
}

const groupMessagesByDate = (messages: Message[] = []): Record<string, Message[]> => {
  const groups: Record<string, Message[]> = {};
  messages.forEach(message => {
    const messageDate = typeof message.time === 'string'
      ? new Date(message.time)
      : message.time;
    if (!(messageDate instanceof Date) || isNaN(messageDate.getTime())) {
      console.warn('Invalid date for message:', message);
      return;
    }
    const dateKey = messageDate.toDateString();
    if (!groups[dateKey]) groups[dateKey] = [];
    groups[dateKey].push(message);
  });
  return groups;
};

const MessageList: React.FC<MessageListProps> = ({
  messages,
  isTyping,
  typingName,
  conversation,
  onDelete,
  onEdit,
  onReply,
  onLike,
  editingId,
  onStartEdit
}) => {
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const messageGroups = useMemo(() => {
    if (!Array.isArray(messages)) return {};
    return groupMessagesByDate(messages);
  }, [messages]);

  useEffect(() => {
    const scrollToBottom = () => {
      if (messagesEndRef.current) {
        messagesEndRef.current.scrollIntoView({ behavior: 'auto', block: 'end' });
      }
    };

    // Defer to next frame after layout settles
    const timeoutId = setTimeout(() => {
      requestAnimationFrame(scrollToBottom);
    }, 50);

    return () => clearTimeout(timeoutId);
  }, [messages.length]);

  return (
    <div className="flex flex-col px-2 sm:px-4 py-6 overflow-x-hidden relative z-10 bg-transparent">
      {Object.entries(messageGroups).map(([date, msgs]) => (
        <motion.div
          key={date}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, ease: "easeOut" }}
        >
          <MessageGroup
            date={date}
            messages={msgs}
            conversation={conversation}
            onDelete={onDelete}
            onEdit={onEdit}
            onReply={onReply}
            onLike={onLike}
            editingId={editingId}
            onStartEdit={onStartEdit}
          />
        </motion.div>
      ))}

      {/* Safe end ref */}
      <div ref={messagesEndRef} className="min-h-[1px]" />

      <AnimatePresence>
        {isTyping && (
          <TypingIndicator name={typingName} image={conversation?.image} />
        )}
      </AnimatePresence>
    </div>
  );
};

export default MessageList;
